import React, { type JSX } from 'react';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import RGL, { WidthProvider } from 'react-grid-layout';
import { DetailsCardSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import { GRID_COLS, GRID_ROWS } from '@fiji/common/src/constants';
import {
    refreshWidget,
    removeWidget,
    setDashboardWidgetIds,
    setLayout,
} from '@fiji/common/src/features/dashboardManagement/dashboardSlice';

import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ConfirmModal, CustomTransComponent, DynamicWidget, Loader } from '../../../components';
import DeleteForever from '@mui/icons-material/DeleteForever';
import Edit from '@mui/icons-material/Edit';
import OpenWith from '@mui/icons-material/OpenWith';
import { Box, Card, CardContent, CardHeader, Divider, Stack, useTheme } from '@mui/material';
import { CustomMenu } from 'components/CustomMenu';
import { useConfirm } from '@fiji/common/src/hooks';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { DriveFileRenameOutline, ErrorOutline, PushPin } from '@mui/icons-material';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { RenameModal } from 'pages/WidgetManagement/WidgetList/RenameModal';
import { getWidgetTypeValidationConfig, isWidgetApplicable, isWidgetConfigured } from '@fiji/common/src/utils/helpers';
import { useRBAC } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { WidgetHeader } from 'pages/WidgetManagement/common';
import {
    useGetDeviceByIdQuery,
    useGetDeviceByProfileQuery,
} from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useGetDashboardByIdQuery } from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { setWidgetDevice } from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';
import { useTranslation } from 'react-i18next';

const ResponsiveReactGridLayout = WidthProvider(RGL);
const EDIT_DASHBOARD_WIDGET_GAP = 16;
const EDIT_DASHBOARD_GRID_MARGIN: [number, number] = [EDIT_DASHBOARD_WIDGET_GAP, EDIT_DASHBOARD_WIDGET_GAP];
const EDIT_DASHBOARD_DEFAULT_MIN_HEIGHT = 4;
const EDIT_DASHBOARD_DEFAULT_MIN_WIDTH = 14;
const EDIT_DASHBOARD_CUSTOM_MIN_HEIGHT = 1;
const EDIT_DASHBOARD_CUSTOM_MIN_WIDTH = 3;
const EDIT_DASHBOARD_DEFAULT_ROW_STEP = EDIT_DASHBOARD_DEFAULT_MIN_HEIGHT + 1;

type Props = {
    dashboardMode: string;
    isLoading?: boolean;
};
export const GridLayout = ({ isLoading, dashboardMode }: Props): JSX.Element => {
    const initialLayoutSanitizedRef = React.useRef<boolean>(false);
    // Track widget dimensions by widget id
    const [widgetDimensions, setWidgetDimensions] = React.useState<Record<string, { width: number; height: number }>>(
        {}
    );
    // Helper to update widget dimensions
    const updateWidgetDimensions = (layoutArr: any[]): any => {
        const newDims: Record<string, { width: number; height: number }> = {};
        layoutArr.forEach((item) => {
            newDims[item.i] = { width: item.w, height: item.h };
        });
        setWidgetDimensions(newDims);
    };
    const navigate = useNavigate();
    const { dashboardId, assignId } = useParams();
    const { state } = useLocation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const currentRealmName = useTypedSelector((selectorState) => selectorState.common.selectedRealm);
    const widgetList = useTypedSelector((selectorState) => selectorState[dashboardMode]?.['widgetList']);
    const layout = useTypedSelector((selectorState) => selectorState[dashboardMode]?.['layout']);
    const loadingState = useTypedSelector((rootState) => rootState[dashboardMode].loaders);
    const renameWidgetModalRef = React.useRef<any>(null);
    const selectedNode = useTypedSelector((rootState) => rootState['common']['selectedNode']);
    const currentOrg = useTypedSelector(selectedOrg);
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);

    const canUpdateUserWidget = hasPermission('widget-management_view-user-widgets');
    const canUpdateSystemWidget = hasPermission('widget-management_edit-system-widgets');

    const { currentData: dashboard }: any = useGetDashboardByIdQuery(dashboardId, {
        skip: !dashboardId || !currentOrg.id,
    });

    const { data: firstDeviceHierarchy } = useGetDeviceByProfileQuery(dashboard?.data?.assignTypeIds?.[0], {
        skip: !dashboard?.data?.assignTypeIds?.[0] || !!assignId,
    });

    const { currentData: deviceData }: any = useGetDeviceByIdQuery(assignId ?? firstDeviceHierarchy?.data?.id, {
        skip: state?.['assignType'] === 'GROUP' || !assignId,
    });
    const handleRemoveWidget = (): void => {
        dispatch(removeWidget(widgetData.id));
        onCancel();
        dispatch(
            setMessageContent({
                isOpen: true,
                message: `${widgetData.name} ${t('COMMON:HAS_BEEN_REMOVED')}`,
            })
        );
    };
    const { data: widgetData, isVisible, onClick, onCancel, onConfirm } = useConfirm(handleRemoveWidget);

    const renderLoader = (type: any): JSX.Element => {
        if (getWidgetTypeValidationConfig(type)['loaderType'] === 'skeleton') {
            return (
                <DetailsCardSkeleton
                    secondarylength={5}
                    {...(getWidgetTypeValidationConfig(type)?.['primarySkeletonCount'] && {
                        primarylength: getWidgetTypeValidationConfig(type)?.['primarySkeletonCount'],
                    })}
                />
            );
        }
        return <Loader size={55} />;
    };

    const getCategoryType = (): string => {
        if (selectedNode?.wrapperDeviceType) {
            return 'DEVICE';
        } else if (selectedNode?.wrapperDeviceType === 'GATEWAY') {
            return 'GATEWAY';
        }
        return 'GROUP';
    };

    const getMenuGroups = (data: any): any => {
        const menuGroups: any = [];
        if ((data?.isSystemWidget && canUpdateSystemWidget) || (!data?.isSystemWidget && canUpdateUserWidget)) {
            menuGroups.push(
                {
                    title: <CustomTransComponent translationKey={'COMMON:EDIT'} />,
                    icon: <Edit />,
                    onClick: (): void => {
                        const { id: widgetId, config, widgetType } = data ?? {};
                        if (
                            selectedNode &&
                            !config?.multiDevice &&
                            isWidgetApplicable(data, state['assignType'] === 'GROUP' ? 'GROUP' : 'DEVICE')
                        ) {
                            dispatch(
                                setWidgetDevice({
                                    data: {
                                        ...selectedNode,
                                        type: getCategoryType(),
                                    },
                                    reset: false,
                                })
                            );
                        }
                        navigate(`/${currentRealmName}/editWidget/${widgetId}`, {
                            state: {
                                typeId: widgetType?.id,
                                mode: 'dashboard',
                                dashboardId: dashboardId,
                                assignType: state?.assignType,
                                assignId: state?.assignId,
                                configurationType: 'edit',
                            },
                        });
                        // Toggled off here, toggled back on by the widget editor's back/save handler.
                        dispatch(setDashboardWidgetIds(widgetId));
                        dispatch(refreshWidget(widgetId));
                    },
                },
                {
                    title: <CustomTransComponent translationKey={'COMMON:RENAME'} />,
                    icon: <DriveFileRenameOutline />,
                    onClick: (): void => {
                        renameWidgetModalRef?.current?.handleModalAction(true, data);
                    },
                }
            );
        }

        menuGroups.push({
            title: <CustomTransComponent translationKey={'COMMON:REMOVE'} />,
            icon: <DeleteForever />,
            onClick: (): any => {
                onClick(data);
            },
        });

        return menuGroups;
    };

    const getActionsBtn = (data: any): any => ({
        id: 'profile-menu',
        menuGroups: getMenuGroups(data),
        menuTitle: '',
        menuSubtitle: '',
    });

    const getWidgetApplicableType = (widget: any): any => {
        if (
            state?.['assignType'] === 'GROUP' ||
            (widget?.widgetType?.id === 'device_list' && deviceData?.data['wrapperDeviceType'] === 'Gateway')
        ) {
            return 'GROUP';
        }
        return 'DEVICE';
    };

    const hasLayoutChanged = (newLayout: any[], currentLayout: any[]): boolean =>
        newLayout.some((newItem: any, index: number) => {
            const currentItem = currentLayout[index];
            return (
                !currentItem ||
                newItem.i !== currentItem.i ||
                newItem.x !== currentItem.x ||
                newItem.y !== currentItem.y ||
                newItem.w !== currentItem.w ||
                newItem.h !== currentItem.h
            );
        });

    const getMinHeightForLayoutItem = (layoutItem: any): number => {
        const configType = widgetList?.find((w: any) => w?.id === layoutItem?.i)?.config?.type;
        const widgetType = widgetList?.find((w: any) => w?.id === layoutItem?.i)?.widgetType?.type;
        if (widgetType === 'CUSTOM') return EDIT_DASHBOARD_CUSTOM_MIN_HEIGHT;
        return configType === 'solidGauge' || configType === 'speedoMeter' ? 5 : 4;
    };

    const getMinWidthForLayoutItem = (layoutItem: any): number => {
        const widgetType = widgetList?.find((w: any) => w?.id === layoutItem?.i)?.widgetType?.type;
        return widgetType === 'CUSTOM' ? EDIT_DASHBOARD_CUSTOM_MIN_WIDTH : EDIT_DASHBOARD_DEFAULT_MIN_WIDTH;
    };

    const getLayoutWithMinConstraints = (layoutArr: any[] = []): any[] =>
        layoutArr.map((item: any) => {
            const minHeight = getMinHeightForLayoutItem(item);
            const minWidth = getMinWidthForLayoutItem(item);
            return {
                ...item,
                w: Math.max(minWidth, item?.w ?? minWidth),
                h: Math.max(minHeight, item?.h ?? minHeight),
                minW: minWidth,
                minH: minHeight,
            };
        });

    const nonPinnedWidgets = widgetList?.filter((item: any) => !item?.config?.isPinned) ?? [];

    const resolveLayoutItem = (
        widgetId: string,
        index: number,
        localLayoutById: Map<string, any>,
        serverLayoutById: Map<string, any>
    ): { x: number; y: number; w: number; h: number } => {
        const minHeight = getMinHeightForLayoutItem({ i: widgetId });
        const minWidth = getMinWidthForLayoutItem({ i: widgetId });
        const fallbackY = index * EDIT_DASHBOARD_DEFAULT_ROW_STEP;
        const existing = localLayoutById.get(widgetId) ?? serverLayoutById.get(widgetId);
        const { x = 0, y = fallbackY, w = minWidth, h = minHeight } = existing ?? {};
        return {
            x,
            y,
            w: Math.max(minWidth, w),
            h: Math.max(minHeight, h),
        };
    };

    const getResolvedGridLayout = (): any[] => {
        const localLayout = layout ?? [];
        const serverLayout = dashboard?.data?.widgetLayout ?? [];

        if (!nonPinnedWidgets.length) return [];

        if (localLayout.length === nonPinnedWidgets.length) {
            return getLayoutWithMinConstraints(localLayout);
        }

        const localLayoutById = new Map<string, any>(localLayout.map((item: any) => [item?.i, item]));
        const serverLayoutById = new Map<string, any>(serverLayout.map((item: any) => [item?.i, item]));

        return nonPinnedWidgets.map((widget: any, index: number) => {
            const widgetId = widget?.id;
            const minHeight = getMinHeightForLayoutItem({ i: widgetId });
            const minWidth = getMinWidthForLayoutItem({ i: widgetId });
            const resolvedLayout = resolveLayoutItem(widgetId, index, localLayoutById, serverLayoutById);

            return {
                i: widgetId,
                x: resolvedLayout.x,
                y: resolvedLayout.y,
                w: resolvedLayout.w,
                h: resolvedLayout.h,
                minW: minWidth,
                minH: minHeight,
            };
        });
    };

    // One-time sanitation for legacy oversized persisted heights.
    React.useEffect(() => {
        if (initialLayoutSanitizedRef.current) return;
        if (!Array.isArray(layout) || !layout.length || !Array.isArray(widgetList) || !widgetList.length) return;

        const sanitizedLayout = layout.map((item: any) => {
            const minHeight = getMinHeightForLayoutItem(item);
            const minWidth = getMinWidthForLayoutItem(item);
            const currentHeight = item?.h ?? minHeight;
            const currentWidth = item?.w ?? minWidth;

            return {
                ...item,
                w: Math.max(minWidth, currentWidth),
                minW: minWidth,
                minH: minHeight,
                h: Math.max(minHeight, currentHeight),
            };
        });

        if (hasLayoutChanged(sanitizedLayout, layout)) {
            dispatch(setLayout(sanitizedLayout));
        }

        updateWidgetDimensions(sanitizedLayout);
        initialLayoutSanitizedRef.current = true;
    }, [layout, widgetList, dispatch]);

    const getCardHeaderTitle = (data?: any, isPinned?: boolean): any => (
        <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={!isPinned ? 'space-between' : 'flex-end'}
            className="padding-0"
            sx={{ height: '48px' }}
        >
            {!isPinned && (
                <Stack direction={'row'} alignItems={'center'}>
                    <Box className="min-width-48 min-height-48 bg-primary-50 border-radius-top-left flex-center-align">
                        <OpenWith color="primary" className="drag-handle cursor-pointer" />
                    </Box>
                </Stack>
            )}
            <Stack direction={'row'} sx={{ zIndex: 99 }}>
                <Box
                    className="min-width-48 custom-position min-height-48 bg-primary-50 border-radius-top-right flex-center-align margin-left-16"
                    sx={{ width: '48px', height: '48px' }}
                >
                    <CustomMenu menuList={getActionsBtn(data)} sx={{ width: '48px', height: '48px' }} />
                </Box>
            </Stack>
        </Stack>
    );

    return (
        <>
            {isLoading && (
                <div className="position-relative custom-loader-height">
                    <Loader size={60} />
                </div>
            )}

            {widgetList
                ?.filter((item: any) => item?.config?.isPinned)
                ?.map((widget: any) => {
                    const { widgetType, id, config, name, updatedAt } = widget ?? {};
                    const Wrapper: any = widgetType?.id === 'map' ? Box : Card;
                    return (
                        <Wrapper
                            key={id}
                            {...(widgetType?.id === 'map' && { sx: { position: 'relative' } })}
                            {...(widgetType?.id === 'command_bar' && {
                                sx: { margin: 2, position: 'relative' },
                            })}
                        >
                            {widgetType?.id === 'map' && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        zIndex: 999,
                                        width: '100%',
                                        height: '48px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: theme?.palette?.primary?.main,
                                            padding: '16px',
                                        }}
                                    >
                                        {name}
                                    </Box>
                                    <Box sx={{ display: 'flex' }}>
                                        <Box
                                            sx={{
                                                height: '48px',
                                                width: '48px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <PushPin
                                                sx={{
                                                    color: theme?.palette?.primary?.main,
                                                    height: '32px',
                                                    width: '32px',
                                                }}
                                            />
                                        </Box>
                                        <Box className="min-width-48 custom-position min-height-48 bg-primary-50 border-radius-top-right flex-center-align">
                                            <CustomMenu menuList={getActionsBtn(widget)} />
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                            {widgetType?.id === 'command_bar' && (
                                <CardHeader
                                    className="border-bottom-1 padding-0"
                                    title={getCardHeaderTitle(widget, config?.isPinned)}
                                />
                            )}
                            {!loadingState[id] && (
                                <CardContent className="padding-0 custom-card-height">
                                    {renderLoader(widgetType?.id)}
                                </CardContent>
                            )}
                            {loadingState[id] && (
                                <DynamicWidget
                                    mode="pinned-edit"
                                    widgetTypeId={widgetType?.id}
                                    {...(widgetType?.id === 'map'
                                        ? {
                                              countKey: 'deviceCount',
                                              widgetId: id,
                                              clustering: false,
                                              widgetData: { ...config, widgetName: name, updatedAt },
                                          }
                                        : {
                                              widgetData: {
                                                  internalCdnJsLink: widgetType?.internalCdnJsLink,
                                                  ...(config
                                                      ? {
                                                            ...config,
                                                            widgetName: name,
                                                            updatedAt,
                                                        }
                                                      : {
                                                            primary: [],
                                                            secondary: [],
                                                            secondaryChannelCount: 6,
                                                            updatedAt,
                                                        }),
                                              },
                                          })}
                                />
                            )}
                        </Wrapper>
                    );
                })}
            {
                // TODO: react-beautiful-dnd package is deprecated and we need to find an alternative package for drag and drop
            }
            {/* @ts-ignore */}
            <ResponsiveReactGridLayout
                className="layout w-100"
                compactType="vertical"
                autoSize={true}
                containerPadding={[EDIT_DASHBOARD_WIDGET_GAP, EDIT_DASHBOARD_WIDGET_GAP]}
                margin={EDIT_DASHBOARD_GRID_MARGIN}
                draggableHandle=".drag-handle"
                layout={getResolvedGridLayout()}
                onLayoutChange={(res): void => {
                    window.dispatchEvent(new Event('resize'));
                    const expectedLength = nonPinnedWidgets.length;
                    const isLayoutLengthMatching = res.length === expectedLength;
                    const isLayoutActuallyChanged = layout.length === expectedLength && hasLayoutChanged(res, layout);

                    if (isLayoutLengthMatching && isLayoutActuallyChanged) {
                        dispatch(setLayout(res));
                    }

                    updateWidgetDimensions(res);
                }}
                onResizeStop={(layoutDemo, newItem) => {
                    const MIN_WIDTH = 14;
                    const MIN_HEIGHT = 4;

                    if (newItem.w < MIN_WIDTH) {
                        newItem.w = MIN_WIDTH;
                    }
                    if (newItem.h < MIN_HEIGHT) {
                        newItem.h = MIN_HEIGHT;
                    }

                    dispatch(setLayout(layoutDemo));
                    updateWidgetDimensions(layoutDemo);
                }}
                onDragStop={(layoutDemo) => {
                    updateWidgetDimensions(layoutDemo);
                }}
                style={{ position: 'absolute' }}
                cols={GRID_COLS}
                rowHeight={GRID_ROWS}
            >
                {Boolean(widgetList?.length) &&
                    widgetList?.map((widget: any) => {
                        const { id, config, widgetType, name, updatedAt } = widget ?? {};
                        return !config?.isPinned ? (
                            <div key={id} style={{ zIndex: 999 }}>
                                <Card className="margin-left-0" sx={{ height: '100%' }}>
                                    <CardHeader
                                        className="border-bottom-1 padding-0"
                                        title={getCardHeaderTitle(widget)}
                                    />
                                    {!loadingState[id] && (
                                        <CardContent className="padding-0 custom-card-height">
                                            {renderLoader(widgetType?.id)}
                                        </CardContent>
                                    )}
                                    {loadingState[id] &&
                                        isWidgetApplicable(widget, getWidgetApplicableType(widget)) &&
                                        isWidgetConfigured(widget) && (
                                            <DynamicWidget
                                                mode="edit"
                                                widgetTypeId={widgetType?.id}
                                                width={widgetDimensions[id]?.width}
                                                height={widgetDimensions[id]?.height}
                                                {...(widgetType?.id === 'map'
                                                    ? {
                                                          countKey: 'deviceCount',
                                                          widgetId: id,
                                                          clustering: false,
                                                          widgetData: {
                                                              ...config,
                                                              widgetTypeId: widgetType?.id,
                                                              widgetName: name,
                                                              updatedAt,
                                                          },
                                                      }
                                                    : {
                                                          widgetData: {
                                                              internalCdnJsLink: widgetType?.internalCdnJsLink,
                                                              ...(config
                                                                  ? {
                                                                        ...config,
                                                                        widgetName: name,
                                                                        widgetTypeId: widgetType?.id,
                                                                        updatedAt,
                                                                    }
                                                                  : {
                                                                        primary: [],
                                                                        secondary: [],
                                                                        secondaryChannelCount: 6,
                                                                        updatedAt,
                                                                    }),
                                                          },
                                                      })}
                                            />
                                        )}

                                    {loadingState[id] &&
                                        !isWidgetApplicable(widget, getWidgetApplicableType(widget)) && (
                                            <Card className="margin-left-0" sx={{ height: '100%' }}>
                                                <WidgetHeader
                                                    mode="edit"
                                                    widgetData={{
                                                        widgetName: name,
                                                        viewAllButton: config?.viewAllButton,
                                                    }}
                                                />
                                                <Divider />
                                                <CardContent>
                                                    <CustomEmptyState
                                                        icon={<ErrorOutline className="height-100 width-100" />}
                                                        title={`${widget?.name} ${t('COMMON:NOT_CONFIGURED')}`}
                                                        className="padding-y-80"
                                                        description={
                                                            <CustomTransComponent
                                                                translationKey={
                                                                    'VIRTUAL_LOAD:WIDGET_TYPE_NOT_SUPPORTED'
                                                                }
                                                            />
                                                        }
                                                    />
                                                </CardContent>
                                            </Card>
                                        )}

                                    {loadingState[id] &&
                                        isWidgetApplicable(widget, getWidgetApplicableType(widget)) &&
                                        !isWidgetConfigured(widget) && (
                                            <Card className="margin-left-0" sx={{ height: '100%' }}>
                                                <WidgetHeader
                                                    mode="edit"
                                                    widgetData={{
                                                        widgetName: name,
                                                        viewAllButton: config?.viewAllButton,
                                                    }}
                                                />
                                                <Divider />
                                                <CardContent>
                                                    <CustomEmptyState
                                                        icon={<ErrorOutline className="height-100 width-100" />}
                                                        title={`${name} ${t('COMMON:NOT_CONFIGURED')}`}
                                                        className="padding-y-80"
                                                        description={
                                                            <CustomTransComponent
                                                                translationKey={
                                                                    'VIRTUAL_LOAD:THIS_WIDGET_TYPE_NOT_CONFIGURED'
                                                                }
                                                            />
                                                        }
                                                    />
                                                </CardContent>
                                            </Card>
                                        )}
                                </Card>
                            </div>
                        ) : null;
                    })}
            </ResponsiveReactGridLayout>
            <ConfirmModal
                data-testid="remove-widget-confirm-modal"
                onCancel={onCancel}
                confirmClick={onConfirm}
                actionButtonText={<CustomTransComponent translationKey={'COMMON:REMOVE'} />}
                header={
                    <CustomTransComponent
                        translationKey={'WIDGETS:REMOVE_WIDGET_MODAL.HEADER'}
                        replace={{
                            widgetName: widgetData?.name,
                        }}
                    />
                }
                description={
                    <CustomTransComponent
                        translationKey={'WIDGETS:REMOVE_WIDGET_MODAL.DESCRIPTION'}
                        replace={{
                            widgetName: widgetData?.name,
                        }}
                    />
                }
                isVisible={isVisible}
            />

            <RenameModal key="da4fqfcq42fg3" ref={renameWidgetModalRef} />
        </>
    );
};
